import { Link } from "react-router-dom"
import { PageHeader } from "~/ui/PageHeader"
import { gql } from "~/__generated__"
import { useQuery } from "@apollo/client"
import { User_AdminCandidatesTableFragment } from "~/__generated__/graphql"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/shadcn/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from "~/ui/tables/Table"
import { Error } from "~/ui/Error"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import { formatDateAndTime } from "~/common/dates"
import { Button, LinkButton } from "~/shadcn/ui/button"
import { usePagination } from "~/common/usePagination"
import { Pagination } from "~/ui/Pagination"
import { adminCandidatePath } from "~/common/paths"
import EllipsisVerticalIcon from "~/images/icons/ellipsis_vertical.svg?react"
import { useSafeMutation } from "~/common/useSafeMutation"
import toast from "react-hot-toast"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Form } from "~/shadcn/ui/form"
import { TextField } from "~/ui/forms/TextField"

const PER_PAGE = 20

const CandidatesTable = ({
  users,
  refetch,
}: {
  users: User_AdminCandidatesTableFragment[]
  refetch: () => void
}) => {
  const [destroyUser] = useSafeMutation(USER_DESTROY_MUTATION)

  const deleteUser = async (userId: string) => {
    const res = await destroyUser({
      variables: {
        input: {
          userId: userId,
        },
      },
    })

    if (res.errors) {
      toast.error("Error deleting user.")
      return
    }

    toast.success("User deleted successfully.")
    refetch()
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">Created Date</TableHead>
          <TableHead className="text-right">Email</TableHead>
          <TableHead className="text-right">Phone</TableHead>
          <TableHead className="text-right">Tests Taken</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow
            key={user.id}
            navigationPath={adminCandidatePath({ userId: user.id })}
          >
            <TableCell>
              <Link
                to={adminCandidatePath({ userId: user.id })}
                className="hover:underline"
              >
                {user.name}
              </Link>
            </TableCell>
            <TableCell className="text-right">
              {formatDateAndTime(user.createdAt)}
            </TableCell>
            <TableCell className="text-right">{user.email}</TableCell>
            <TableCell className="text-right">{user.phone}</TableCell>
            <TableCell className="text-right">
              {user.candidateTestsCount}
            </TableCell>
            <TableCell className="flex items-center justify-end gap-2.5 text-right">
              <LinkButton to={adminCandidatePath({ userId: user.id })}>
                View
              </LinkButton>
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <EllipsisVerticalIcon className="w-4 h-4 hover:text-primary" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => deleteUser(user.id)}>
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const formSchema = z.object({
  query: z.string().optional(),
})

export const AdminCandidatesScreen = () => {
  const { after, paginate, page } = usePagination({ perPage: PER_PAGE })
  const { data, error, loading, refetch } = useQuery(
    CANDIDATES_QUERY_DOCUMENT,
    {
      variables: { first: PER_PAGE, after },
    }
  )
  const users = data?.adminCandidates?.edges.map((e) => e.node) || []

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      query: "",
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    refetch({
      query: values.query,
      first: PER_PAGE,
      after: null,
    })
  }

  if (loading) return <LoadingIndicatorCentered />
  if (error || !data) return <Error message="Error loading candidates." />

  return (
    <div className="container mx-auto flex flex-col gap-8 mt-8 pb-12">
      <PageHeader title="Candidates">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex items-center gap-4"
          >
            <TextField
              control={form.control}
              name="query"
              label=""
              showLabel={false}
              placeholder="Search by name, email, or AI overview text"
              style={{ width: "300px" }}
            />
            <Button size="sm" onClick={() => {}}>
              Search
            </Button>
          </form>
        </Form>
      </PageHeader>
      {users.length > 0 ? (
        <CandidatesTable users={users} refetch={refetch} />
      ) : (
        <div>No candidates yet.</div>
      )}

      {data.adminCandidates.pageCount > 1 && (
        <Pagination
          page={page}
          pageCount={data.adminCandidates.pageCount}
          paginate={paginate}
        />
      )}
    </div>
  )
}

gql(`
  fragment User_AdminCandidatesTable on User {
    id
    name
    email
    phone
    createdAt
    candidateTestsCount
  }
`)

const CANDIDATES_QUERY_DOCUMENT = gql(`
  query AdminCandidates($query: String, $first: Int!, $after: String) {
    adminCandidates(query: $query, first: $first, after: $after) {
      totalCount
      pageCount(first: $first)
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          ...User_AdminCandidatesTable
        }
      }
    }
  }
`)

const USER_DESTROY_MUTATION = gql(`
  mutation UserDestroy($input: UserDestroyInput!) {
    userDestroy(input: $input) {
      user {
        id
      }
    }
  }
`)
